import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import homepageImg from '../../assets/kitoBuddy/default.png';
import assessmentIcon from '../../assets/designSystem/icons/StaticPages/HomePage/assessment.svg';
import firstAidIcon from '../../assets/designSystem/icons/StaticPages/HomePage/firstAid.svg';
import signsIcon from '../../assets/designSystem/icons/StaticPages/HomePage/signs.svg';
import progressIcon from '../../assets/designSystem/icons/StaticPages/HomePage/progress.svg';
// import KitoBalloon from '../../components/DataDisplay/Balloon';
import KitoBuddy from '../../components/Buddy';
import KitoCard from '../../components/DataDisplay/Card';
import ChatBubble from '../../components/Chat/Chat';
import newSkillsIcon from '../../assets/new-skills.png';
import styles from './HomePageAdult.module.css';

export default function HomePageAdult() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const learnCarouselRef = useRef(null);
  const [learnIndex, setLearnIndex] = useState(0);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const cards = useMemo(
    () => [
      {
        key: 'assessment',
        title: t('staticPages.homePageAdult.cards.assessment'),
        tone: 'help',
        color: 'yellow',
        image: assessmentIcon,
        onClick: () => {
          navigate('/assessment');
        },
      },
      {
        key: 'firstAid',
        title: t('staticPages.homePageAdult.cards.firstAid'),
        tone: 'help',
        color: 'orange',
        image: firstAidIcon,
        onClick: () => {
          navigate('/firstaid-adult');
        },
      },
      {
        key: 'signs',
        title: t('staticPages.homePageAdult.cards.signsAndSymptoms'),
        tone: 'learn',
        image: signsIcon,
        iconAlt: t('staticPages.homePageAdult.cards.signsAndSymptoms'),
        onClick: () => {
          navigate('/symptoms');
        },
      },
      {
        key: 'progress',
        title: t('staticPages.homePageAdult.cards.childProgress'),
        tone: 'learn',
        color: 'blue',
        image: progressIcon,
        onClick: () => {
          navigate('/childProgress');
        },
      },
      {
        key: 'skills',
        title: t('staticPages.homePageAdult.cards.skills'),
        tone: 'learn',
        image: newSkillsIcon,
        onClick: () => {
          navigate('/new-skills');
        },
      },
    ],
    [navigate, t]
  );
  const learnCards = useMemo(() => cards.filter((c) => c.tone === 'learn'), [cards]);

  const updateCarouselBounds = useCallback(() => {
    const carousel = learnCarouselRef.current;
    if (!carousel) return;
    const maxScroll = carousel.scrollWidth - carousel.clientWidth;
    const currentScroll = Math.round(carousel.scrollLeft);
    const carouselItems = Array.from(carousel.children);

    if (carouselItems.length > 0) {
      const closestIndex = carouselItems.reduce((closest, item, index) => {
        const closestDistance = Math.abs(carouselItems[closest].offsetLeft - currentScroll);
        const itemDistance = Math.abs(item.offsetLeft - currentScroll);

        return itemDistance < closestDistance ? index : closest;
      }, 0);

      setLearnIndex(closestIndex);
    } else {
      setLearnIndex(0);
    }

    setIsAtStart(currentScroll <= 1);
    setIsAtEnd(currentScroll >= maxScroll - 1 || maxScroll <= 0);
  }, []);

  const scrollToLearnIndex = useCallback((index) => {
    const carousel = learnCarouselRef.current;
    if (!carousel) return;
    const targetCard = carousel.children[index];
    if (targetCard) {
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;
      const targetScroll = Math.min(targetCard.offsetLeft, Math.max(maxScroll, 0));

      carousel.scrollTo({ left: targetScroll, behavior: 'smooth' });
      setLearnIndex(index);
      setIsAtStart(targetScroll <= 1);
      setIsAtEnd(targetScroll >= maxScroll - 1 || maxScroll <= 0);
    }
  }, []);

  const getCurrentLearnIndex = useCallback(() => {
    const carousel = learnCarouselRef.current;
    if (!carousel) return learnIndex;
    const currentScroll = Math.round(carousel.scrollLeft);
    const carouselItems = Array.from(carousel.children);

    if (carouselItems.length === 0) {
      return learnIndex;
    }

    return carouselItems.reduce((closest, item, index) => {
      const closestDistance = Math.abs(carouselItems[closest].offsetLeft - currentScroll);
      const itemDistance = Math.abs(item.offsetLeft - currentScroll);

      return itemDistance < closestDistance ? index : closest;
    }, 0);
  }, [learnIndex]);

  const handleLearnCarouselNext = () => {
    const currentIndex = getCurrentLearnIndex();
    const nextIndex = Math.min(currentIndex + 1, learnCards.length - 1);
    if (nextIndex === currentIndex) return;
    scrollToLearnIndex(nextIndex);
  };

  const handleLearnCarouselPrev = () => {
    const currentIndex = getCurrentLearnIndex();
    const prevIndex = Math.max(currentIndex - 1, 0);
    if (prevIndex === currentIndex) return;
    scrollToLearnIndex(prevIndex);
  };

  useEffect(() => {
    const carousel = learnCarouselRef.current;

    updateCarouselBounds();

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateCarouselBounds);
    if (resizeObserver && carousel) {
      resizeObserver.observe(carousel);
    }
    window.addEventListener('resize', updateCarouselBounds);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateCarouselBounds);
    };
  }, [learnCards.length, updateCarouselBounds]);

  return (
    <section className={styles.homeContainer} aria-label="Home Page">
      <div className={styles.homeContent}>
      <KitoBuddy aria-hidden="true" stretch textVariant="title">
        {t('staticPages.homePageAdult.greeting')}
      </KitoBuddy>
        <div className={styles.sectionsContainer}>
          <div className={styles.section}>
            <p className="bold">{t('staticPages.homePageAdult.sections.getHelpTitle')}</p>
            <div className={styles.sectionSubtitle}>{t('staticPages.homePageAdult.sections.getHelpSubtitle')}</div>

            <div className={styles.grid}>
              {cards
                .filter((c) => c.tone === 'help')
                .map(({ key, ...c }) => (
                  <KitoCard short key={key} {...c} />
                ))}
            </div>
          </div>

          <div className={`${styles.section} ${styles.learnSection}`}>
            <div className={styles.sectionTitle}>{t('staticPages.homePageAdult.sections.learnTitle')}</div>
            <div className={styles.sectionSubtitle}>{t('staticPages.homePageAdult.sections.learnSubtitle')}</div>

            <div className={styles.carouselShell}>
              <button
                type="button"
                className={styles.carouselPrev}
                aria-label={t('staticPages.homePageAdult.carousel.previousItem')}
                onClick={handleLearnCarouselPrev}
                disabled={isAtStart}
              >
                <span aria-hidden="true" />
              </button>

              <div
                className={styles.learnCarousel}
                ref={learnCarouselRef}
                aria-label={t('staticPages.homePageAdult.sections.learnTitle')}
                onScroll={updateCarouselBounds}
              >
                {learnCards.map(({ key, ...c }) => (
                  <div className={styles.carouselItem} key={key}>
                    <KitoCard short {...c} />
                  </div>
                ))}
              </div>

              <button
                type="button"
                className={styles.carouselNext}
                aria-label={t('staticPages.homePageAdult.carousel.nextItem')}
                onClick={handleLearnCarouselNext}
                disabled={isAtEnd}
              >
                <span aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
